const API_URL = process.env.REACT_APP_API_URL;
const CHATS_URL = `${API_URL}/api/chats`;
const MESSAGES_URL = `${API_URL}/api/messages`;

export const fetchChats = async (page = 1, limit = 50) => {
    try {
        const response = await fetch(
            `${CHATS_URL}?page=${page}&limit=${limit}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            }
        );

        if (!response.ok) {
            throw new Error('Failed to fetch chats');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching chats:', error);
        throw error;
    }
};

export const handleStreamResponse = async (response, onUpdate) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        buffer = lines.pop() || '';

        for (const line of lines) {
            if (line.trim() === '') continue;
            if (!line.startsWith('data: ')) continue;

            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
                const parsed = JSON.parse(data);
                onUpdate(parsed);
            } catch (e) {
                console.error('Error parsing SSE data:', e);
            }
        }
    }
};

export const sendMessage = async (chatId, data, onUpdate) => {
    try {
        const isNewChat = !chatId || chatId === 'new';
        const endpoint = `${MESSAGES_URL}/${isNewChat ? 'new' : chatId}`;

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const errorData = await response.json() || { error: 'Failed to send message' };
            throw errorData;
        }

        await handleStreamResponse(response, onUpdate);
    } catch (error) {
        console.error('Error sending message:', error);
        throw error;
    }
};

export const regenerateMessage = async (chatId, data, onUpdate) => {
    try {
        const endpoint = `${MESSAGES_URL}/${chatId}`;

        const newData = {
            content: null,
            model: data.modelId,
            parentId: data.parentId,
            isRegenerate: true,
        }

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(newData),
        });

        if (!response.ok) {
            const error = await response.json() || { error: 'Failed to regenerate message' };
            throw error;
        }

        await handleStreamResponse(response, onUpdate);
    } catch (error) {
        console.error('Error regenerating message:', error);
        throw error;
    }
};

export const fetchChatHistory = async chatId => {
    try {
        const response = await fetch(`${MESSAGES_URL}/${chatId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (response.status === 404) {
            throw new Error('chat_not_found');
        }

        if (!response.ok) {
            throw new Error('Failed to fetch chat history');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching chat history:', error);
        throw error;
    }
};

export const stopConversation = async chatId => {
    try {
        const response = await fetch(`${MESSAGES_URL}/stop_conversation`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ chatId }),
        });

        if (!response.ok) {
            throw new Error('Failed to stop generation');
        }

        return await response.json();
    } catch (error) {
        console.error('Error stopping conversation:', error);
        throw error;
    }
};

export const fetchTools = async messageId => {
    try {
        const response = await fetch(`${MESSAGES_URL}/${messageId}/tools`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to fetch tools by message id');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching tools by message id:', error);
        throw error;
    }
};

export const submitMessageFeedback = async (messageId, data) => {
    try {
        const response = await fetch(`${MESSAGES_URL}/${messageId}/feedback`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            throw new Error('Failed to submit feedback');
        }

        return await response.json();
    } catch (error) {
        console.error('Error submitting message feedback:', error);
        throw error;
    }
};
